import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { combineLatest, EMPTY, Observable, of, catchError, distinctUntilChanged, filter, map, switchMap } from 'rxjs';

import { BOOKING_STEPS } from '@fcom/common/config/booking-config';
import { LanguageService } from '@fcom/ui-translate';
import { getBookingStepLink } from '@fcom/common/utils/booking-common.utils';
import {
  ConfigService,
  SentryLogger,
  GlobalBookingTravelClass,
  LocationRouteCffService,
  BUS_CONNECTION_LOCATION_CODES,
  isPresent,
} from '@fcom/core';
import { add, isEmptyObjectOrHasEmptyValues, mapValues, TimeUnit, toISO8601, unique, valuesOf } from '@fcom/core/utils';
import { CmsDestination } from '@fcom/core-api';
import { DatePriceList, PriceList } from '@fcom/dapi/api/models';
import { finShare } from '@fcom/rx';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import { getPricesForOffers, postPricesForFlights } from '@fcom/dapi/api/instantsearch-functions';

import { OndMarketingDataService } from './ond-marketing-data.service';
import {
  OndLocationData,
  OndLocationsResponse,
  OndMarketingOffer,
  OndPairEnhancedResponse,
  OndPairRequestBody,
} from '../interfaces';
import { constructOndHeadingPath, getCurrentCountry, isCountryCode } from './utils';

export const OFFERS_CACHE_TIME_IN_MILLIS = 60 * 1000; // 1 minute

@Injectable()
export class OndMarketingOffersService {
  private sentryLogger = inject(SentryLogger);
  private ondMarketingDataService = inject(OndMarketingDataService);
  private languageService = inject(LanguageService);
  private dapiService = inject(DapiService);
  private configService = inject(ConfigService);
  private locationRouteCffService = inject(LocationRouteCffService);

  private flightsDataCache$: Record<string, { expires: number; stream$: Observable<OndMarketingOffer[]> }> = {};
  private offersDataCache$: Record<string, { expires: number; stream$: Observable<OndMarketingOffer[]> }> = {};
  private locationsDataCache$: Record<string, { expires: number; stream$: Observable<OndLocationsResponse> }> = {};

  fetchLocationPairs(requestBody: OndPairRequestBody): Observable<OndPairEnhancedResponse> {
    return this.ondMarketingDataService.fetchLocationPairs(requestBody).pipe(
      filter((pairs) => !isEmptyObjectOrHasEmptyValues(pairs)),
      switchMap((pairs) => {
        const translationsToFetch = valuesOf(pairs)
          .reduce((locationCodes, pair) => locationCodes.concat(pair.origin, pair.destination), [])
          .filter(unique);

        return this.getLocationData(translationsToFetch).pipe(
          map((locations) => {
            return mapValues(pairs, (pair) => {
              return {
                ...pair,
                originCityName: locations[pair.origin]?.cityName,
                originCountryName: locations[pair.origin]?.country,
                originCountryCode: locations[pair.origin]?.countryCode,
                destinationCityName: locations[pair.destination]?.cityName,
                destinationCountryName: locations[pair.destination]?.country,
                destinationCountryCode: locations[pair.destination]?.countryCode,
              };
            });
          })
        );
      }),
      catchError((e: unknown) => this.handleError<OndPairEnhancedResponse>(e, 'Error getting ond location pairs')),
      finShare()
    );
  }

  getDestinationLocationCode(content$: Observable<CmsDestination>): Observable<string> {
    return content$.pipe(
      filter<CmsDestination>(Boolean),
      map((content) => content.locationCode),
      filter<string>(Boolean),
      finShare()
    );
  }

  getHeading(
    originLocationCode$: Observable<string>,
    destinationLocationCode$: Observable<string>,
    ondOffers$: Observable<OndMarketingOffer[]>,
    headingType: string
  ): Observable<string> {
    return combineLatest([originLocationCode$, destinationLocationCode$, ondOffers$]).pipe(
      switchMap(([originLocationCode, destinationLocationCode, offers]) => {
        const origin = isCountryCode(originLocationCode) ? offers[0]?.originCountryName : offers[0]?.originCityName;
        const destination = isCountryCode(destinationLocationCode)
          ? offers[0]?.destinationCountryName
          : offers[0]?.destinationCityName;

        return this.languageService.translate(
          constructOndHeadingPath(headingType, {
            destination: destinationLocationCode,
            origin: originLocationCode,
          }),
          {
            origin,
            destination,
            originLocationCode: originLocationCode,
            destinationLocationCode: destinationLocationCode,
          }
        );
      }),
      distinctUntilChanged(),
      finShare()
    );
  }

  getOffersOrFlightData(
    originLocationCode$: Observable<string>,
    destinationLocationCode$: Observable<string>,
    links$: Observable<OndPairEnhancedResponse>
  ): Observable<OndMarketingOffer[]> {
    const destinationLocationCodes$ = destinationLocationCode$.pipe(
      switchMap((locationCode) => {
        if (locationCode) {
          return of([locationCode]);
        } else {
          return links$.pipe(
            map((links) => {
              return valuesOf(links).map((link) => link.destination);
            })
          );
        }
      })
    );
    return combineLatest([originLocationCode$, destinationLocationCodes$]).pipe(
      switchMap(([originLocationCode, destinationLocationCodes]) => {
        if (originLocationCode && !isCountryCode(originLocationCode)) {
          return this.getOfferData(
            destinationLocationCodes.filter((dest) => !BUS_CONNECTION_LOCATION_CODES.includes(dest)),
            originLocationCode
          );
        } else {
          return this.getFlightsData(
            [
              isCountryCode(originLocationCode)
                ? originLocationCode
                : getCurrentCountry(this.languageService.langValue),
            ],
            destinationLocationCodes,
            isCountryCode(originLocationCode) ? 'from' : 'to'
          );
        }
      }),
      map((offers) =>
        offers
          .filter((off) => isPresent(off.destinationCityName))
          .reduce((acc, offer) => {
            const existingOfferIndex = acc.findIndex(
              (i) => i.destinationLocationCode === offer.destinationLocationCode
            );

            if (existingOfferIndex === -1) {
              acc.push(offer);
            } else if (+offer.price.amount < +acc[existingOfferIndex].price.amount) {
              acc[existingOfferIndex] = offer;
            }

            return acc;
          }, [])
      ),
      finShare()
    );
  }

  private getFlightsData(
    departureLocationCodes: string[],
    destinationLocationCodes: string[],
    direction?: string
  ): Observable<OndMarketingOffer[]> {
    const cacheKey = departureLocationCodes.join('-') + destinationLocationCodes.join('-') + direction;
    if (!this.flightsDataCache$[cacheKey] || Date.now() >= this.flightsDataCache$[cacheKey].expires) {
      const stream$: Observable<OndMarketingOffer[]> = this.dapiService
        .invoke$(postPricesForFlights, this.configService.cfg.instantSearchUrl, {
          body: {
            departureLocationCodes,
            destinationLocationCodes,
            locale: this.languageService.langValue,
          },
        })
        .pipe(
          map((data) =>
            valuesOf(data.prices)
              .map((destinations) => valuesOf(destinations))
              .flat()
              .map(this.flightPriceMapper)
          ),
          switchMap((offers) => this.enhanceOffersWithTranslations(offers)),
          catchError(() => EMPTY),
          finShare()
        );
      this.flightsDataCache$[cacheKey] = {
        stream$,
        expires: Date.now() + OFFERS_CACHE_TIME_IN_MILLIS,
      };
    }

    return this.flightsDataCache$[cacheKey].stream$;
  }

  private getOfferData(
    destinationLocationCodes: string[],
    departureLocationCode: string
  ): Observable<OndMarketingOffer[]> {
    const cacheKey = departureLocationCode + destinationLocationCodes.join('-');
    if (!this.offersDataCache$[cacheKey] || Date.now() >= this.offersDataCache$[cacheKey].expires) {
      const stream$: Observable<OndMarketingOffer[]> = this.dapiService
        .invoke$(getPricesForOffers, this.configService.cfg.instantSearchUrl, {
          destinationLocationCodes,
          departureLocationCode,
          departureDate: toISO8601(add(new Date(), 1, TimeUnit.day)),
          numberOfDays: 60,
          lengthOfStay: '3-10',
          travelClasses: [GlobalBookingTravelClass.ECONOMY.toLowerCase()],
        })
        .pipe(
          map((data) =>
            valuesOf(data.prices)
              .map((destinations) => valuesOf(destinations))
              .flat(2)
              .map(this.offersPriceMapper)
          ),
          switchMap((offers) => this.enhanceOffersWithTranslations(offers)),
          catchError((e: unknown) =>
            this.handleError<OndMarketingOffer[]>(e, 'Error getting ond marketing offers data')
          ),
          finShare()
        );
      this.offersDataCache$[cacheKey] = {
        stream$,
        expires: Date.now() + OFFERS_CACHE_TIME_IN_MILLIS,
      };
    }

    return this.offersDataCache$[cacheKey].stream$;
  }

  private enhanceOffersWithTranslations(offers: OndMarketingOffer[]): Observable<OndMarketingOffer[]> {
    const translationsToFetch = offers
      .reduce(
        (locationCodes, offer) => locationCodes.concat(offer.originLocationCode, offer.destinationLocationCode),
        []
      )
      .filter(unique);
    return this.getLocationData(translationsToFetch).pipe(
      map((locations) => {
        return offers.map((offer) => {
          return {
            ...offer,
            originCityName: (locations[offer.originLocationCode] as OndLocationData)?.cityName,
            originCountryName: (locations[offer.originLocationCode] as OndLocationData)?.country,
            originCountryCode: (locations[offer.originLocationCode] as OndLocationData)?.countryCode,
            destinationCityName: (locations[offer.destinationLocationCode] as OndLocationData)?.cityName,
            destinationCountryName: (locations[offer.destinationLocationCode] as OndLocationData)?.country,
            destinationCountryCode: (locations[offer.destinationLocationCode] as OndLocationData)?.countryCode,
          };
        });
      })
    );
  }

  getAirportName(cityName: string, locale: string): Observable<string> {
    if (!cityName || !locale || cityName.length < 3) {
      return of(undefined);
    }
    return this.locationRouteCffService.bestGuessFor(cityName, locale).pipe(
      map((data) => data?.title),
      finShare()
    );
  }

  getLocationData(destinationCodeArray: string[]): Observable<OndLocationsResponse> {
    const cacheKey = destinationCodeArray.join('-');
    if (!this.locationsDataCache$[cacheKey] || Date.now() >= this.locationsDataCache$[cacheKey].expires) {
      const stream$ =
        destinationCodeArray.length === 0
          ? of({}).pipe()
          : this.ondMarketingDataService.fetchLocations(destinationCodeArray).pipe(
              catchError((e: unknown) => this.handleError<OndLocationsResponse>(e, 'Error getting ond location data')),
              finShare()
            );
      this.locationsDataCache$[cacheKey] = {
        stream$,
        expires: Date.now() + OFFERS_CACHE_TIME_IN_MILLIS,
      };
    }
    return this.locationsDataCache$[cacheKey].stream$;
  }

  private flightPriceMapper = (priceData: PriceList): OndMarketingOffer => {
    const originLocationCode = priceData.from;
    const destinationLocationCode = priceData.to;
    const pricesForEconomy = priceData.travelClassPrices.find((travelClass) => travelClass.travelClass === 'Economy');
    return {
      originLocationCode,
      destinationLocationCode,
      departureDate: pricesForEconomy.fromDate,
      returnDate: pricesForEconomy.toDate,
      price: {
        amount: pricesForEconomy.price.toString(),
        currencyCode: priceData.currency,
      },
      link: getBookingStepLink(this.languageService.langValue, BOOKING_STEPS.ENTRY, {
        origin: originLocationCode,
        destination: destinationLocationCode,
        departureDate: pricesForEconomy.fromDate,
        returnDate: pricesForEconomy.toDate,
      }),
    };
  };

  private offersPriceMapper = (priceData: DatePriceList): OndMarketingOffer => {
    const originLocationCode = priceData.from;
    const destinationLocationCode = priceData.to;
    const pricesForEconomy = priceData.travelClassPrices.find((travelClass) => travelClass.travelClass === 'Economy');
    return {
      originLocationCode,
      destinationLocationCode,
      departureDate: priceData.fromDate,
      returnDate: priceData.toDate,
      price: {
        amount: pricesForEconomy.price.toString(),
        currencyCode: priceData.currency,
      },
      link: getBookingStepLink(this.languageService.langValue, BOOKING_STEPS.ENTRY, {
        origin: originLocationCode,
        destination: destinationLocationCode,
        departureDate: priceData.fromDate,
        returnDate: priceData.toDate,
      }),
    };
  };

  private handleError<T>(error: unknown, message: string): Observable<T> {
    if (error instanceof HttpErrorResponse && error.error?.error !== 'No location pairs found.') {
      this.sentryLogger.error(message, { error });
    }
    return EMPTY;
  }
}
